import { Prisma, Badge as PrismaBadge } from "@prisma/client";
import Image from "next/image";
import useIsMobile from "~/hooks/useIsMobile";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
    useTooltip,
} from "./shadcn/Tooltip";
import Link from "next/link";
import { If } from "./v3/Conditional";

const BadgeImage = ({
    badge,
    size,
}: {
    badge?: Partial<PrismaBadge> & { count?: number };
    size: number;
}) => {
    return (
        <Image
            key={badge?.id}
            alt={`badge-${badge?.name}`}
            width={size}
            height={size}
            src={`${badge?.path ?? ""}`}
        />
    );
};

const LinkedBadge = ({
    linked,
    communitySlug,
    badge,
    size,
}: {
    linked: boolean;
    communitySlug: string | undefined;
    badge?: Partial<PrismaBadge> & { count?: number };
    size: number;
}) => {
    return (
        <>
            <If condition={!!linked && !!communitySlug}>
                <Link href={`/${communitySlug}`}>
                    <BadgeImage badge={badge} size={size} />
                </Link>
            </If>
            <If condition={!linked || !communitySlug}>
                <BadgeImage badge={badge} size={size} />
            </If>
        </>
    );
};

const Badge = ({
    badge,
    size = 50,
    tooltip = true,
    linked,
}: {
    badge?: (
        | Prisma.BadgeGetPayload<{
              include: { validCommunities: { select: { slug: true } } };
          }>
        | Partial<PrismaBadge>
    ) & { count?: number };
    size?: number;
    tooltip?: boolean;
    linked?: boolean;
}) => {
    const { showTooltip, handleTooltipClick } = useTooltip();
    const isMobile = useIsMobile();
    const tooltipInfo =
        badge?.description !== "" ? badge?.description : badge?.name;

    if (!tooltip) return <BadgeImage badge={badge} size={size} />;

    const communitySlug =
        badge && "validCommunities" in badge
            ? badge.validCommunities.at(0)?.slug
            : undefined;

    return (
        <div className="relative">
            <TooltipProvider delayDuration={0}>
                {isMobile ? (
                    <Tooltip open={showTooltip}>
                        <TooltipTrigger onClick={handleTooltipClick}>
                            <LinkedBadge
                                linked={!!linked}
                                communitySlug={communitySlug}
                                badge={badge}
                                size={size}
                            />
                        </TooltipTrigger>
                        <TooltipContent sideOffset={-100}>
                            <p className="text-[12px]">{tooltipInfo}</p>
                        </TooltipContent>
                    </Tooltip>
                ) : (
                    <Tooltip>
                        <TooltipTrigger>
                            <LinkedBadge
                                linked={!!linked}
                                communitySlug={communitySlug}
                                badge={badge}
                                size={size}
                            />
                        </TooltipTrigger>

                        <TooltipContent sideOffset={-100}>
                            <p className="text-[12px]">{tooltipInfo}</p>
                        </TooltipContent>
                    </Tooltip>
                )}
            </TooltipProvider>
            {badge?.count && badge?.count > 1 && (
                <span className="absolute -bottom-1 left-[15px] flex h-[15px] w-[20px] items-center justify-center rounded-md bg-green-600 text-[11px] font-bold leading-none text-black">
                    {badge.count}
                </span>
            )}
        </div>
    );
};

export default Badge;
